import { modal, button } from "../../design/components.js";

/** Reason vocab — mirrors the app.aid.mod.flag `flagReason` lexicon vocab. */
const REASONS = [
  "falseReport", "spam", "harassment", "dangerousAdvice",
  "harvestAttempt", "doxxing", "illegal", "other",
] as const;

export interface FlagInput {
  subject: string; // incident TID or identity did being flagged
  reason: string;  // flagReason vocab
  note: string;    // optional plaintext; sealed before publish (empty = omit)
}

export interface FlagSheetCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  subject: string;
  onSubmit: (input: FlagInput) => Promise<void>;
  onClose: () => void;
}

/** Flag bottom sheet (GUI §6 "deliberately thin"; moderation spec §4). Private,
 *  silent submit — no public mob mechanics, downweighting is invisible to authors. */
export function flagSheet(ctx: FlagSheetCtx): HTMLElement {
  const t = ctx.t;
  const body = document.createElement("div");
  body.className = "flag-sheet__body";
  const sheet = modal({ title: t("flag.title"), content: body, onClose: ctx.onClose, sheetId: "flag" });

  let reason = "";
  let note = "";

  function done(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "done");
    const p = document.createElement("p");
    p.className = "bx-body";
    p.textContent = t("flag.done");
    const close = button({ label: t("flag.submit"), variant: "primary", onClick: ctx.onClose });
    close.setAttribute("data-action", "done-close");
    wrap.append(p, close);
    return wrap;
  }

  function form(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "form");
    const intro = document.createElement("p");
    intro.className = "bx-microlabel";
    intro.textContent = t("flag.intro");
    wrap.append(intro);

    const grid = document.createElement("div");
    grid.className = "flag-sheet__reasons";
    const submitBtn = button({ label: t("flag.submit"), variant: "primary", onClick: () => void submit() });
    submitBtn.setAttribute("data-action", "submit");
    (submitBtn as HTMLButtonElement).disabled = !reason;

    for (const r of REASONS) {
      const b = button({
        label: t(`flag.reason.${r}`),
        variant: r === reason ? "primary" : "secondary",
        onClick: () => { reason = r; body.replaceChildren(form()); },
      });
      b.setAttribute("data-reason", r);
      grid.append(b);
    }
    wrap.append(grid);

    const noteInput = document.createElement("input");
    noteInput.type = "text";
    noteInput.maxLength = 200;
    noteInput.setAttribute("data-note", "");
    noteInput.value = note;
    noteInput.addEventListener("input", () => { note = noteInput.value; });
    wrap.append(noteInput);
    wrap.append(submitBtn);
    return wrap;
  }

  async function submit(): Promise<void> {
    if (!reason) return;
    try {
      await ctx.onSubmit({ subject: ctx.subject, reason, note });
      body.replaceChildren(done());
    } catch {
      const err = document.createElement("p");
      err.className = "bx-body flag-sheet__error";
      err.textContent = t("flag.error");
      body.append(err);
    }
  }

  body.replaceChildren(form());
  return sheet;
}
